import { useEffect, useState, useRef, useContext, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { getSubjectName, getLessonName, logoutLearner, } from '@/api/kuizy'
import { logout as apiLogout, myLearner, learnerToken, learnerMemberships } from '@/api/auth'
import { getMeCached, getMyProfileCached, clearUserCache } from '@/api/userCache'
import { LogOut, UserRound, Menu, X } from 'lucide-react'
import { LayoutContext } from '@/layout/KuizyLayout'
import Sidebar from '@/layout/Sidebar'
import { API_BASE } from '@/settings/master'
import { motion, AnimatePresence } from 'framer-motion'
import { useKuizyTheme } from "@/styles/kuizyTheme";
// import InviteBell from '@/widgets/InviteBell'
import SwitchOrgModal from '@/widgets/SwitchOrgModal'
import { createPortal } from 'react-dom'

export default function Topbar() {
  const { mode, toggleMode, roles } = useContext(LayoutContext)
  const isAdminRole = roles.includes('MASTER_ADMIN') || roles.includes('ADMIN') || roles.includes('CREATOR')
  const nav = useNavigate()
  const { pathname } = useLocation()
  const { cid: cidRaw, lid: lidRaw, tid: tidRaw } = useParams()
  const cid = cidRaw ? decodeURIComponent(cidRaw) : null
  const lid = lidRaw ? decodeURIComponent(lidRaw) : null
  const { setTheme } = useKuizyTheme()

  const [profile, setProfile] = useState({ id: '', avatar: '', avatarUrl: '', name: '' })
  const didInit = useRef(false)
  useEffect(() => { 
    if (didInit.current) return
    didInit.current = true
      ; (async () => {
        try {
          const me = await getMeCached() 

          const id = me.user_id
          let name = me.username || `${id}`
          let avatarText = name[0]?.toUpperCase() || '?'
          try {
            const p = await getMyProfileCached() 
            const disp = (p.display_name || `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.trim() || name).trim()
            name = disp || name
            avatarText = name[0]?.toUpperCase() || avatarText
            const au = p.avatar_url
              ? p.avatar_url
              : (p.avatar_path
                ? (/^(http|data:)/.test(p.avatar_path) ? p.avatar_path : `${API_BASE}${p.avatar_path}`)
                : '')
            setProfile({ id, name, avatar: avatarText, avatarUrl: au })
          } catch {
            setProfile({ id, name, avatar: avatarText, avatarUrl: '' })
          }
        } catch {
          nav('/login', { replace: true })
        }
      })()
  }, [nav])



  const [contentName, setContentName] = useState(cid || '')
  const [lessonName, setLessonName] = useState(lid || '')
  useEffect(() => { if (cid) getSubjectName(cid).then(({ data }) => setContentName(data?.name || cid)).catch(() => setContentName(cid)) }, [cid])
  useEffect(() => { if (lid) getLessonName(lid).then(({ data }) => setLessonName(data?.lesson || lid)).catch(() => setLessonName(lid)) }, [lid])

  const inBank = pathname.includes('/bank')
  const inBuilder = pathname.includes('/tests')
  const inNewExam = pathname.includes('/create-exam')
  const inTestList = pathname.endsWith('/tests')
  const inTestBuilder = pathname.endsWith('/tests/new')
  const inClassList = pathname.endsWith('/class')
  const inGroupList = pathname.endsWith('/group')
  const inLearnerList = pathname.endsWith('/learners')
  const inOverview = pathname.endsWith('/player')
  const inLearner = pathname.endsWith('/learn/portal')
  const inLearnerReport = pathname.endsWith('/learners/reports-results')
  const inOrganization = pathname.endsWith('/organization')
  const inRole = pathname.endsWith('/role')
  const inCourseList = pathname.endsWith('/course')
  const inMyCourseList = pathname.endsWith('/my-courses')
  const inDashboard = pathname.endsWith('/dashboard')

  const crumbs = [{ label: 'Kuizy', name: (inLearner || inLearnerReport || inOverview || inClassList || inCourseList || inMyCourseList || inDashboard || inTestList || inGroupList || inRole || inOrganization || inLearnerList) ? 'หน้าหลัก' : 'เนื้อหา', path: '/content', color: 'var(--color-primary)' }]
  if (cid) crumbs.push({ label: 'Content', name: contentName, path: `/content/${encodeURIComponent(cidRaw)}/lessons`, color: 'var(--color-primary)' })
  if (!inBuilder) {
    if (lid) crumbs.push({ label: 'Lessons', name: lessonName, path: `/content/${encodeURIComponent(cidRaw)}/lessons/${encodeURIComponent(lidRaw)}/bank`, color: 'var(--color-primary)' })
    if (inBank) crumbs.push({ label: 'Exams Bank', name: 'คลังข้อสอบ', path: pathname, color: 'var(--color-primary)' })
    if (inNewExam) crumbs.push({ label: 'Add Exams', name: 'เพิ่มข้อสอบ', path: pathname, color: 'var(--color-primary)' })
  } else {
    const listPath = cid ? (lid ? `/content/${encodeURIComponent(cidRaw)}/lessons/${encodeURIComponent(lidRaw)}/tests` : `/content/${encodeURIComponent(cidRaw)}/tests`) : '/tests'
    const builderPath = cid ? (lid ? `/content/${encodeURIComponent(cidRaw)}/lessons/${encodeURIComponent(lidRaw)}/tests/new` : `/content/${encodeURIComponent(cidRaw)}/tests/new`) : ''
    if (inTestList) crumbs.push({ label: 'Tests set', name: 'ชุดข้อสอบ', path: listPath, color: 'var(--color-secondary)' })
    if (inTestBuilder) crumbs.push({ label: 'Test builder', name: 'สร้างชุดข้อสอบ', path: builderPath, color: 'var(--color-secondary)' })
  }
  if (!cid) {
    if (pathname.endsWith('/edit')) crumbs.push({ label: 'Tests Edit', name: 'แก้ไขชุดข้อสอบ', path: `/tests/${encodeURIComponent(tidRaw)}/edit`, color: 'var(--color-secondary)' })
    if (inClassList) crumbs.push({ label: 'Class', name: 'จัดการห้องเรียน', path: '/class', color: 'var(--color-primary)' })
    if (inCourseList) crumbs.push({ label: 'Course', name: 'จัดการหลักสูตร', path: '/course', color: 'var(--color-primary)' })
    if (inMyCourseList) crumbs.push({ label: 'Add exam sets to the Course', name: 'เพิ่มชุดข้อสอบเข้าหลักสูตร', path: '/my-course', color: 'var(--color-primary)' })
    if (inDashboard) crumbs.push({ label: 'Dashboard', name: 'แดชบอร์ด', path: '/dashboard', color: 'var(--color-primary)' })
    if (inGroupList) crumbs.push({ label: 'Groups', name: 'จัดการกลุ่มผู้เรียน', path: '/group', color: 'var(--color-primary)' })
    if (inLearnerList) crumbs.push({ label: 'Learners', name: 'จัดการผู้เรียน', path: '/learners', color: 'var(--color-primary)' })
    if (inOverview) crumbs.push({ label: 'Players', name: 'จัดการผู้เล่น', path: '/player', color: 'var(--color-primary)' })
    if (inLearner) crumbs.push({ label: 'Learners', name: 'ผู้เรียน', path: '/learn/portal', color: 'var(--color-primary)' })
    if (inLearnerReport) crumbs.push({ label: 'Reports', name: 'รายงาน', path: '/learners/reports-results', color: 'var(--color-primary)' })
    if (inOrganization) crumbs.push({ label: 'Organization Information', name: 'จัดการข้อมูลองค์กร', path: '/organization', color: 'var(--color-primary)' })
    if (inRole) crumbs.push({ label: 'Roles', name: 'จัดการผู้ใช้งานระบบ', path: '/role', color: 'var(--color-primary)' })
  }

  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuRef = useRef(null)
  useEffect(() => {
    const fn = e => open && menuRef.current && !menuRef.current.contains(e.target) && setOpen(false)
    window.addEventListener('click', fn)
    return () => window.removeEventListener('click', fn)
  }, [open])

  useEffect(() => {
    document.body.style.overflow = (drawerOpen || confirmOpen) ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [drawerOpen, confirmOpen])

  const handleLogout = async () => {
    try { await apiLogout() } catch { }
    try { await logoutLearner(); } catch { }
    // localStorage.clear();
    clearUserCache()
    nav('/login', { replace: true })
  }

  const [hasLearner, setHasLearner] = useState(false)
  const [learnerData, setLearnerData] = useState(null)
  useEffect(() => {
    learnerMemberships()
      .then(r => {
        const items = r?.data?.items.length > 0
        if (!items) {
          setHasLearner(false)
          return
        }
        setHasLearner(true)
        setLearnerData(items)
      })
      .catch(() => setHasLearner(false))

  }, [])

  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [switchOrgOpen, setSwitchOrgOpen] = useState(false)

  const fetchOrgs = async ({ q = keyword, reset = false } = {}) => {
    if (loading) return

    setLoading(true)
    const nextPage = reset ? 1 : page

    const res = await learnerMemberships({
      q,
      page: nextPage,
      limit: 10,
    })

    const newItems = res?.data?.items || []

    setItems(prev =>
      reset ? newItems : [...prev, ...newItems]
    )
    setHasMore(newItems.length === 10)
    setPage(nextPage + 1)
    setKeyword(q)
    setLoading(false)
  }

  // useEffect(() => {
  //   const org_active = localStorage.getItem('orgId')
  //   myLearner(org_active)
  //     .then(r => {
  //       const items = r?.data?.items
  //       if (!items) {
  //         setHasLearner(false)
  //         return
  //       }
  //       setHasLearner(true)
  //     })
  //     .catch(() => setHasLearner(false))
  // }, [])

  const waitFrame = () => new Promise(res => requestAnimationFrame(res))
  const ensureModeThenNavigate = useCallback(
    async (targetMode, to) => {
      localStorage.setItem('viewMode', targetMode)
      if (mode !== targetMode) toggleMode()
      for (let i = 0; i < 8; i++) {
        await waitFrame()
        const ls = localStorage.getItem('viewMode')
        if (ls === targetMode) break
      }
      nav(to, { replace: true })
    },
    [mode, toggleMode, nav]
  )

const goLearner = async (org) => {
  try {
    const orgId = org.org_id
    console.log('org : ', org)

    const res = await learnerToken(orgId)

    if (res?.data?.success === true) {
      setTheme('white')
      localStorage.setItem('learner', '1')
      localStorage.setItem('orgId', res?.data?.learner_org_id)
      await ensureModeThenNavigate('user', '/learn/home')
    } else {
      console.warn('learnerToken failed:', res?.data)
      // จะ toast error ตรงนี้ก็ได้
    }

  } catch (err) {
    console.error('goLearner error:', err)
    // toast error ได้ตรงนี้
  }
}


  const onSwitchChange = async e => {
    setSwitchOrgOpen(true)
    fetchOrgs({ reset: true })
  }

  const avatarNode = profile.avatarUrl
    ? <img src={/^(http|data:)/.test(profile.avatarUrl) ? profile.avatarUrl : `${API_BASE}${profile.avatarUrl}`} alt="me" className="w-8 h-8 rounded-full object-cover" />
    : <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">{profile.avatar}</span>

  return (
    <>
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-4">
        <div className="flex items-center gap-2 min-w-0">
          <button className="md:hidden p-2 -ml-1 rounded-lg hover:bg-gray-100 active:bg-gray-200" onClick={() => setDrawerOpen(true)}>
            <Menu size={20} />
          </button>
          <nav className="flex items-center gap-4 overflow-x-auto scrollbar-none no-scrollbar min-w-0">
            {crumbs.map((c, i) => (
              <div key={i} className={`grid ${c.name ? 'grid-rows-[16px_20px]' : 'grid-rows-[20px]'} min-w-0 pr-8 relative shrink-0`}>
                <Link to={c.path} className="text-[10px] uppercase tracking-[0.02em] text-gray-500 hover:underline whitespace-nowrap truncate">{c.label}</Link>
                {c.name && <Link to={c.path} className="text-[0.9rem] font-semibold whitespace-nowrap truncate hover:underline" style={{ color: c.color }}>{c.name}</Link>}
                {i < crumbs.length - 1 && <span className="absolute top-1/2 right-3 w-2 h-2 border-r-2 border-b-2 border-gray-400 -rotate-45 -translate-y-1/2" />}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 ml-2">

          {isAdminRole && hasLearner && (
            <div className="hidden md:flex items-center gap-2">
              <span className={`text-xs font-semibold text-primary `}>{mode === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้สอน'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" onChange={onSwitchChange} className="sr-only peer" />
                <div className="w-10 h-5 rounded-full bg-primary transition-colors duration-200"></div>
                <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 peer-checked:translate-x-5"></div>
              </label>
              <span className={`text-xs font-semibold text-gray-400 `}>ผู้เรียน</span>
            </div>
          )}
          {/* <InviteBell token={profile.id} /> */}

          <div className="relative" ref={menuRef}>
            <button
              onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
              className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full pl-2 pr-3 py-1 hover:bg-gray-200 focus:ring-2 focus:ring-primary"
            >
              {avatarNode}
              <span className="max-w-[96px] sm:max-w-[140px] truncate text-sm font-medium text-gray-800">{profile.name}</span>
              <svg width="10" height="6" viewBox="0 0 10 6" className={`transition-transform ${open ? 'rotate-180' : ''}`}><path d="M1 1l4 4 4-4" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
            </button>

            {open && (
              <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black/5 z-30 animate-fade-in">
                <button
                  onClick={() => { setOpen(false); nav('/user-profiles') }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-gray-700 hover:bg-gray-100 text-sm"
                >
                  <UserRound className="w-4 h-4" /> ข้อมูลผู้ใช้ของฉัน
                </button>
                <div className="h-px bg-gray-100 my-1" />
                <button
                  onClick={() => { setOpen(false); setConfirmOpen(true) }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 text-sm"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <SwitchOrgModal
        open={switchOrgOpen}
        title="เลือกองค์กร"
        items={items}
        loading={loading}
        hasMore={hasMore}
        onSearch={q => fetchOrgs({ q, reset: true })}
        onLoadMore={() => fetchOrgs({ q: keyword })}
        onClose={() => setSwitchOrgOpen(false)}
        onSelect={async org => {
          setSwitchOrgOpen(false)
          if (isAdminRole) {
            await goLearner(org)
          }
        }}
      />


      {typeof document !== 'undefined' && ReactDOM.createPortal(
        confirmOpen ? (
          <div className="fixed inset-0 z-[100] grid place-items-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmOpen(false)} />
            <div className="relative z-[101] bg-white rounded-xl shadow-2xl p-5 w-[90vw] max-w-[360px]">
              <div className="text-base font-semibold mb-1">ออกจากระบบ</div>
              <div className="text-sm text-gray-600 mb-4">ยืนยันการออกจากระบบหรือไม่</div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmOpen(false)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
                <button onClick={handleLogout} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm">ออกจากระบบ</button>
              </div>
            </div>
          </div>
        ) : null,
        document.getElementById('modal-root') || document.body
      )}

      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <AnimatePresence>
          {drawerOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-50 md:hidden bg-black/40 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setDrawerOpen(false)}
              />
              <motion.div
                className="fixed inset-y-0 left-0 z-50 md:hidden w-[84vw] max-w-[300px] bg-white shadow-2xl border-r border-gray-200 rounded-r-2xl pt-[env(safe-area-inset-top)]"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', stiffness: 380, damping: 36 }}
              >
                <div className="h-12 px-3 flex items-center justify-between border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <img src="/images/app-logo.png" className="h-8 w-auto" alt="Kuizy" />
                  </div>
                  <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200">
                    <X size={18} />
                  </button>
                </div>
                <div className="h-[calc(100%-3rem)] flex flex-col">
                  <Sidebar drawer onClose={() => setDrawerOpen(false)} />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.getElementById('drawer-root') || document.body
      )}
    </>
  )
}
